import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../store';
import { Ic } from '../icons';
import {
  DISCIPLINES, buildMatchPayload, createEmptyMatch, emptyMap, emptyPlayer,
  matchToDraft, slugify, validateMatchDraft,
} from './matchEditor';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

const STATUS_OPTIONS = [
  ['scheduled', 'Скоро'], ['live', 'Live'], ['finished', 'Завершён'],
];

function Field({ label, children, hint, wide = false }) {
  return <label className={`mc-admin-field${wide ? ' wide' : ''}`}>
    <span>{label}</span>{children}{hint ? <small>{hint}</small> : null}
  </label>;
}

function NumberInput({ value, onChange, step = 1, min = 0, label }) {
  return <input
    className="fed-input" type="number" inputMode="decimal" min={min} step={step}
    aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}
  />;
}

function MatchList({ matches, selectedSlug, discipline, onDiscipline, onSelect, loading }) {
  const visible = useMemo(
    () => matches.filter((match) => discipline === 'all' || match.discipline === discipline),
    [matches, discipline],
  );
  return <aside className="fed-admin-card mc-admin-list">
    <header><div><span className="fed-eyebrow">Матчи</span><h3>Все матчи</h3></div><b>{visible.length}</b></header>
    <div className="mc-admin-filters">
      {[['all', 'Все'], ['cs2', 'CS2'], ['mlbb', 'MLBB']].map(([value, label]) =>
        <button key={value} type="button" className={discipline === value ? 'on' : ''} onClick={() => onDiscipline(value)}>{label}</button>)}
    </div>
    <div className="mc-admin-match-stack">
      {loading ? <p className="mc-admin-empty">Загружаем матчи…</p> : null}
      {!loading && !visible.length ? <p className="mc-admin-empty">Матчей пока нет. Создайте первый матч кнопкой сверху.</p> : null}
      {visible.map((match) => {
        const [a, b] = match.teams || [];
        return <button
          key={match.slug} type="button" onClick={() => onSelect(match)}
          className={`mc-admin-match${selectedSlug === match.slug ? ' on' : ''}`}
        >
          <span className="mc-admin-match-top"><i className={match.discipline}>{match.discipline === 'mlbb' ? 'MLBB' : 'CS2'}</i><em className={match.status}>{match.status === 'live' ? 'LIVE' : match.status === 'finished' ? 'ЗАВЕРШЁН' : 'СКОРО'}</em></span>
          <strong>{a?.name && b?.name ? `${a.name} — ${b.name}` : match.title}</strong>
          <span className="mc-admin-match-score"><b>{a?.maps ?? 0}</b><i>:</i><b>{b?.maps ?? 0}</b><small>{match.stage || match.tournament_key}</small></span>
        </button>;
      })}
    </div>
  </aside>;
}

function TeamEditor({ team, index, discipline, onChange }) {
  const config = DISCIPLINES[discipline];
  const set = (key, value) => onChange({ ...team, [key]: value });
  return <section className={`mc-admin-team team-${index}`}>
    <header><span>Команда {index + 1}</span><b>{team.tag || `T${index + 1}`}</b></header>
    <div className="mc-admin-team-meta">
      <Field label="Название"><input className="fed-input" value={team.name} onChange={(event) => set('name', event.target.value)} placeholder={index ? 'Команда B' : 'Команда A'} /></Field>
      <Field label="Тег"><input className="fed-input" value={team.tag} maxLength={10} onChange={(event) => set('tag', event.target.value)} placeholder={index ? 'BRV' : 'ALP'} /></Field>
    </div>
    <div className="mc-admin-team-score">
      <Field label={config.seriesScore}><NumberInput label={config.seriesScore} value={team.maps} onChange={(value) => set('maps', value)} /></Field>
      <Field label={config.liveScore}><NumberInput label={config.liveScore} value={team.score} onChange={(value) => set('score', value)} /></Field>
    </div>
  </section>;
}

function MapsEditor({ draft, setDraft }) {
  const config = DISCIPLINES[draft.discipline];
  const updateMap = (index, patch) => setDraft((current) => ({
    ...current, maps: current.maps.map((map, mapIndex) => mapIndex === index ? { ...map, ...patch } : map),
  }));
  const removeMap = (index) => setDraft((current) => ({
    ...current, maps: current.maps.filter((_, mapIndex) => mapIndex !== index),
  }));
  const addMap = () => setDraft((current) => ({
    ...current, maps: [...current.maps, emptyMap(current.discipline, current.maps.length)],
  }));
  return <section className="fed-admin-card mc-admin-section">
    <header className="mc-admin-section-head"><div><span className="fed-eyebrow">Серия</span><h3>{config.unit === 'Игра' ? 'Игры серии' : 'Карты серии'}</h3></div><button type="button" className="fed-btn-ghost" onClick={addMap}><Ic name="plus" size={14} />Добавить</button></header>
    <div className="mc-admin-map-list">
      {draft.maps.map((map, index) => <div className="mc-admin-map-row" key={map._key}>
        <span className="mc-admin-row-number">{index + 1}</span>
        <input className="fed-input" aria-label={`Название ${config.unit.toLowerCase()} ${index + 1}`} value={map.name} onChange={(event) => updateMap(index, { name: event.target.value })} />
        <NumberInput label="Счёт команды 1" value={map.a} onChange={(value) => updateMap(index, { a: value })} />
        <span className="mc-admin-colon">:</span>
        <NumberInput label="Счёт команды 2" value={map.b} onChange={(value) => updateMap(index, { b: value })} />
        <select className="fed-input" aria-label="Статус" value={map.status} onChange={(event) => updateMap(index, { status: event.target.value })}>
          <option value="pending">Следующая</option><option value="live">Live</option><option value="finished">Завершена</option>
        </select>
        <button type="button" className="mc-admin-icon danger" onClick={() => removeMap(index)} aria-label={`Удалить ${config.unit.toLowerCase()}`}><Ic name="trash" size={16} /></button>
      </div>)}
      {!draft.maps.length ? <p className="mc-admin-empty">Добавьте хотя бы одну {config.unit.toLowerCase()}.</p> : null}
    </div>
  </section>;
}

function PlayersEditor({ draft, setDraft }) {
  const config = DISCIPLINES[draft.discipline];
  const updatePlayer = (key, patch) => setDraft((current) => ({
    ...current, players: current.players.map((player) => player._key === key ? { ...player, ...patch } : player),
  }));
  const removePlayer = (key) => setDraft((current) => ({
    ...current, players: current.players.filter((player) => player._key !== key),
  }));
  const addPlayer = (team) => setDraft((current) => ({
    ...current, players: [...current.players, emptyPlayer(team)],
  }));
  return <section className="fed-admin-card mc-admin-section">
    <header className="mc-admin-section-head"><div><span className="fed-eyebrow">Статистика</span><h3>Игроки</h3></div><small>Пустые строки не публикуются</small></header>
    <div className="mc-admin-rosters">
      {[0, 1].map((team) => <section className={`mc-admin-roster team-${team}`} key={team}>
        <header><div><span>Состав</span><h4>{draft.teams[team]?.name || `Команда ${team + 1}`}</h4></div><button type="button" className="fed-btn-ghost" onClick={() => addPlayer(team)}><Ic name="plus" size={13} />Игрок</button></header>
        <div className="mc-admin-player-scroll">
          <div className="mc-admin-player-labels"><span>Никнейм</span>{config.playerFields.map(([, label]) => <span key={label}>{label}</span>)}<span /></div>
          {draft.players.filter((player) => player.team === team).map((player) => <div className="mc-admin-player-row" key={player._key}>
            <input className="fed-input" aria-label="Никнейм" value={player.nick} onChange={(event) => updatePlayer(player._key, { nick: event.target.value })} placeholder="Player" />
            {config.playerFields.map(([field, label]) => field === 'hero'
              ? <input key={field} className="fed-input" aria-label={label} value={player[field]} onChange={(event) => updatePlayer(player._key, { [field]: event.target.value })} placeholder="Герой" />
              : <NumberInput key={field} label={label} value={player[field]} step={field === 'rating' || field === 'adr' ? 0.01 : 1} onChange={(value) => updatePlayer(player._key, { [field]: value })} />)}
            <button type="button" className="mc-admin-icon danger" onClick={() => removePlayer(player._key)} aria-label="Удалить игрока"><Ic name="x" size={15} /></button>
          </div>)}
        </div>
      </section>)}
    </div>
  </section>;
}

const SOURCE_STATUS = {
  waiting: 'Ожидает подключение', connected: 'Подключён', stale: 'Нет свежих данных',
  offline: 'Отключён', paused: 'Автоматика приостановлена',
};

function SourcePanel({ source, token, busy, onRotate, onToggle }) {
  const lastSeen = source?.last_ingest_at
    ? new Date(source.last_ingest_at).toLocaleString('ru-RU') : 'событий ещё не было';
  return <section className="fed-admin-card mc-admin-section mc-admin-source">
    <header className="mc-admin-section-head">
      <div><span className="fed-eyebrow">Источник данных</span><h3>Подключение игрового адаптера</h3></div>
      <span className={`mc-admin-source-status ${source?.status || 'waiting'}`}><i />{SOURCE_STATUS[source?.status] || 'Загрузка…'}</span>
    </header>
    <div className="mc-admin-source-grid">
      <div><span>Source ID</span><b>{source?.source_id || 'не создан'}</b></div>
      <div><span>Последовательность</span><b>{source?.last_sequence ?? 0}</b></div>
      <div><span>Последнее событие</span><b>{lastSeen}</b></div>
      <div><span>Авторизация</span><b>{source?.auth_mode === 'per_match' ? 'Отдельный токен' : source?.auth_mode === 'legacy_global' ? 'Общий токен' : 'Не настроена'}</b></div>
    </div>
    {source?.last_error ? <div className="mc-admin-source-error"><Ic name="info" size={16} />{source.last_error}</div> : null}
    {token ? <div className="mc-admin-source-token">
      <p>Скопируйте токен сейчас: после закрытия страницы он больше не показывается.</p>
      <div><input className="fed-input" readOnly value={token} /><button type="button" className="fed-btn-ghost" onClick={() => navigator.clipboard?.writeText(token)}>Копировать</button></div>
    </div> : null}
    <div className="mc-admin-source-actions">
      <button type="button" className="fed-btn-ghost" disabled={busy} onClick={onRotate}>{source?.configured ? 'Перевыпустить токен' : 'Создать токен'}</button>
      <button type="button" className={source?.auto_updates_enabled ? 'mc-admin-pause' : 'fed-btn-primary'} disabled={busy || !source} onClick={() => onToggle(!source?.auto_updates_enabled)}>
        {source?.auto_updates_enabled ? 'Приостановить автоматику' : 'Возобновить автоматику'}
      </button>
    </div>
  </section>;
}

function MatchEditor({ draft, setDraft, creating, busy, source, sourceToken, onRotateSource, onToggleSource, onSave, onCancel, onDelete }) {
  const config = DISCIPLINES[draft.discipline];
  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const updateTeam = (index, team) => setDraft((current) => ({
    ...current, teams: current.teams.map((item, teamIndex) => teamIndex === index ? team : item),
  }));
  const changeDiscipline = (discipline) => setDraft((current) => {
    const next = createEmptyMatch(discipline);
    return { ...next, tournament_key: current.tournament_key, title: current.title, slug: current.slug,
      stage: current.stage, scheduled_at: current.scheduled_at, stream_url: current.stream_url,
      teams: current.teams };
  });
  return <main className="mc-admin-editor">
    <section className="fed-admin-card mc-admin-editor-head">
      <div><span className="fed-eyebrow">{creating ? 'Новый матч' : draft.discipline === 'mlbb' ? 'MLBB · ручной ввод' : 'CS2 · ручной ввод'}</span><h2>{creating ? 'Создание матча' : draft.title}</h2><p>{creating ? 'Заполните команды и привязку к турниру.' : `Версия данных обновится после публикации · ${draft.slug}`}</p></div>
      <div className="mc-admin-head-actions">
        {!creating ? <button type="button" className="mc-admin-delete" onClick={onDelete} disabled={busy}><Ic name="trash" size={16} />Удалить</button> : null}
        <button type="button" className="fed-btn-ghost" onClick={onCancel} disabled={busy}>Отмена</button>
        <button type="button" className="fed-btn-primary" onClick={onSave} disabled={busy}><Ic name="check" size={17} />{busy ? 'Сохраняем…' : creating ? 'Создать матч' : 'Опубликовать'}</button>
      </div>
    </section>

    {!creating ? <SourcePanel source={source} token={sourceToken} busy={busy} onRotate={onRotateSource} onToggle={onToggleSource} /> : null}

    <section className="fed-admin-card mc-admin-section">
      <header className="mc-admin-section-head"><div><span className="fed-eyebrow">Основное</span><h3>Параметры матча</h3></div></header>
      <div className="mc-admin-discipline">
        {Object.entries(DISCIPLINES).map(([key, item]) => <button key={key} type="button" disabled={!creating} className={draft.discipline === key ? 'on' : ''} onClick={() => changeDiscipline(key)}><Ic name={key === 'mlbb' ? 'phone' : 'gamepad'} size={19} /><span>{item.label}</span></button>)}
      </div>
      <div className="mc-admin-form-grid">
        <Field label="ID турнира" hint="Должен совпадать с id турнира на витрине"><input className="fed-input" value={draft.tournament_key} onChange={(event) => setField('tournament_key', event.target.value)} placeholder={draft.discipline === 'mlbb' ? 'mlbb-mca-quals2-2026' : 'cs2-open-2026'} /></Field>
        <Field label="Название матча"><input className="fed-input" value={draft.title} onChange={(event) => setField('title', event.target.value)} placeholder="Team A — Team B" /></Field>
        {creating ? <Field label="Slug" hint="Можно оставить пустым — создастся автоматически"><input className="fed-input" value={draft.slug} onChange={(event) => setField('slug', slugify(event.target.value))} placeholder="team-a-vs-team-b" /></Field> : null}
        <Field label="Этап"><input className="fed-input" value={draft.stage} onChange={(event) => setField('stage', event.target.value)} placeholder="Полуфинал · Upper bracket" /></Field>
        <Field label="Формат серии"><select className="fed-input" value={draft.best_of} onChange={(event) => setField('best_of', Number(event.target.value))}>{[1, 2, 3, 5, 7].map((value) => <option value={value} key={value}>BO{value}</option>)}</select></Field>
        <Field label="Дата и время"><input className="fed-input" type="datetime-local" value={draft.scheduled_at} onChange={(event) => setField('scheduled_at', event.target.value)} /></Field>
        <Field label="Ссылка на трансляцию" wide><input className="fed-input" type="url" value={draft.stream_url} onChange={(event) => setField('stream_url', event.target.value)} placeholder="https://youtube.com/..." /></Field>
      </div>
      <div className="mc-admin-status"><span>Статус публикации</span><div>{STATUS_OPTIONS.map(([value, label]) => <button type="button" key={value} className={`${value}${draft.status === value ? ' on' : ''}`} onClick={() => setField('status', value)}>{label}</button>)}</div></div>
    </section>

    <section className="fed-admin-card mc-admin-section">
      <header className="mc-admin-section-head"><div><span className="fed-eyebrow">Live</span><h3>Текущий счёт</h3></div><small>Эти значения видны зрителям сразу после публикации</small></header>
      <div className="mc-admin-teams"><TeamEditor team={draft.teams[0]} index={0} discipline={draft.discipline} onChange={(team) => updateTeam(0, team)} /><span className="mc-admin-vs">VS</span><TeamEditor team={draft.teams[1]} index={1} discipline={draft.discipline} onChange={(team) => updateTeam(1, team)} /></div>
      <div className="mc-admin-current">
        <Field label={config.currentUnit}><input className="fed-input" value={draft.current_map} onChange={(event) => setField('current_map', event.target.value)} placeholder={draft.discipline === 'mlbb' ? 'Игра 2' : 'Mirage'} /></Field>
        {draft.discipline === 'cs2'
          ? <Field label="Текущий раунд"><NumberInput label="Текущий раунд" value={draft.round} onChange={(value) => setField('round', value)} /></Field>
          : <Field label="Время игры"><input className="fed-input" value={draft.game_time} onChange={(event) => setField('game_time', event.target.value)} placeholder="12:34" /></Field>}
      </div>
    </section>

    <MapsEditor draft={draft} setDraft={setDraft} />
    <PlayersEditor draft={draft} setDraft={setDraft} />

    <section className="fed-admin-card mc-admin-section">
      <header className="mc-admin-section-head"><div><span className="fed-eyebrow">Ход матча</span><h3>Событие к публикации</h3></div></header>
      <Field label="Текст события" hint="Если оставить пустым, сохранится только обновление статистики" wide><input className="fed-input" value={draft.event_text} onChange={(event) => setField('event_text', event.target.value)} placeholder={draft.discipline === 'mlbb' ? 'Команда A забирает Лорда' : 'Команда A выигрывает 12-й раунд'} /></Field>
      <div className="mc-admin-bottom-actions"><button type="button" className="fed-btn-primary" disabled={busy} onClick={onSave}><Ic name="check" size={17} />{busy ? 'Сохраняем…' : creating ? 'Создать матч' : 'Опубликовать изменения'}</button></div>
    </section>
  </main>;
}

export default function AdmMatchCenter() {
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [source, setSource] = useState(null);
  const [sourceToken, setSourceToken] = useState('');

  const load = async (preferredSlug) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/match-center/matches`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Не удалось загрузить матчи');
      const next = data.matches || [];
      setMatches(next);
      if (preferredSlug) {
        const match = next.find((item) => item.slug === preferredSlug);
        if (match) { setSelected(match); setDraft(matchToDraft(match)); }
      }
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'API матч-центра недоступен' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected?.slug || creating) return undefined;
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`${API_URL}/match-center/matches/${encodeURIComponent(selected.slug)}/source`, { headers: authHeaders() });
        const data = await response.json();
        if (active && response.ok && data.source) setSource(data.source);
      } catch (_) { /* The last known source state remains visible. */ }
    };
    refresh();
    const timer = window.setInterval(refresh, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selected?.slug, creating]);

  const selectMatch = async (match) => {
    setCreating(false); setSelected(match); setDraft(matchToDraft(match)); setNotice(null);
    setSource(null); setSourceToken('');
    try {
      const [matchResponse, sourceResponse] = await Promise.all([
        fetch(`${API_URL}/match-center/matches/${encodeURIComponent(match.slug)}`),
        fetch(`${API_URL}/match-center/matches/${encodeURIComponent(match.slug)}/source`, { headers: authHeaders() }),
      ]);
      const data = await matchResponse.json();
      const sourceData = await sourceResponse.json();
      if (matchResponse.ok && data.match) { setSelected(data.match); setDraft(matchToDraft(data.match)); }
      if (sourceResponse.ok && sourceData.source) setSource(sourceData.source);
    } catch (_) { /* Снимка из списка достаточно для редактирования. */ }
  };

  const startCreate = (discipline = filter === 'mlbb' ? 'mlbb' : 'cs2') => {
    setCreating(true); setSelected(null); setDraft(createEmptyMatch(discipline)); setNotice(null);
    setSource(null); setSourceToken('');
  };

  const rotateSource = async () => {
    if (!selected) return;
    if (source?.configured && !window.confirm('Старый токен перестанет работать. Выпустить новый?')) return;
    setBusy(true); setNotice(null);
    try {
      const response = await fetch(`${API_URL}/match-center/matches/${encodeURIComponent(selected.slug)}/source/token`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Не удалось создать токен');
      setSource(data.source); setSourceToken(data.token || '');
      setNotice({ type: 'success', text: 'Токен источника создан. Скопируйте и сохраните его.' });
    } catch (error) { setNotice({ type: 'error', text: error.message }); }
    finally { setBusy(false); }
  };

  const toggleSource = async (enabled) => {
    if (!selected) return;
    setBusy(true); setNotice(null);
    try {
      const response = await fetch(`${API_URL}/match-center/matches/${encodeURIComponent(selected.slug)}/source`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ auto_updates_enabled: enabled }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Не удалось изменить режим источника');
      setSource(data.source);
      setNotice({ type: 'success', text: enabled ? 'Автоматические обновления возобновлены' : 'Автоматика приостановлена — матч можно править вручную' });
    } catch (error) { setNotice({ type: 'error', text: error.message }); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft) return;
    const errors = validateMatchDraft(draft);
    if (errors.length) { setNotice({ type: 'error', text: errors.join('. ') }); return; }
    const payload = buildMatchPayload(draft);
    if (creating && !payload.slug) {
      const stamp = Date.now().toString().slice(-6);
      payload.slug = `${slugify(`${payload.snapshot.teams[0].name}-vs-${payload.snapshot.teams[1].name}`) || 'match'}-${stamp}`;
    }
    setBusy(true); setNotice(null);
    try {
      const url = creating ? `${API_URL}/match-center/matches` : `${API_URL}/match-center/matches/${encodeURIComponent(selected.slug)}`;
      const response = await fetch(url, { method: creating ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Не удалось сохранить матч');
      setCreating(false); setSelected(data.match); setDraft(matchToDraft(data.match));
      setNotice({ type: 'success', text: creating ? 'Матч создан и опубликован' : 'Изменения опубликованы' });
      await load(data.match.slug);
    } catch (error) { setNotice({ type: 'error', text: error.message || 'Ошибка сохранения' }); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected || !window.confirm(`Удалить матч «${selected.title}»? Восстановить его из админки не получится.`)) return;
    setBusy(true); setNotice(null);
    try {
      const response = await fetch(`${API_URL}/match-center/matches/${encodeURIComponent(selected.slug)}`, { method: 'DELETE', headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Не удалось удалить матч');
      setSelected(null); setDraft(null); setCreating(false); setSource(null); setSourceToken('');
      setNotice({ type: 'success', text: 'Матч удалён' }); await load();
    } catch (error) { setNotice({ type: 'error', text: error.message || 'Ошибка удаления' }); }
    finally { setBusy(false); }
  };

  const cancel = () => {
    if (selected) { setDraft(matchToDraft(selected)); setCreating(false); }
    else { setDraft(null); setCreating(false); }
    setNotice(null);
  };

  return <div className="mc-admin-page">
    <section className="fed-admin-card mc-admin-hero">
      <div><span className="fed-eyebrow">Ручное управление</span><h1>Центр матчей</h1><p>Создавайте матчи, ведите live-счёт и публикуйте статистику игроков для CS2 и MLBB.</p></div>
      <button type="button" className="fed-btn-primary" onClick={() => startCreate()} disabled={busy}><Ic name="plus" size={17} />Создать матч</button>
    </section>
    {notice ? <div className={`mc-admin-notice ${notice.type}`} role="status"><Ic name={notice.type === 'success' ? 'check' : 'info'} size={17} />{notice.text}</div> : null}
    <div className="mc-admin-workspace">
      <MatchList matches={matches} selectedSlug={selected?.slug} discipline={filter} onDiscipline={setFilter} onSelect={selectMatch} loading={loading} />
      {draft
        ? <MatchEditor draft={draft} setDraft={setDraft} creating={creating} busy={busy} source={source} sourceToken={sourceToken} onRotateSource={rotateSource} onToggleSource={toggleSource} onSave={save} onCancel={cancel} onDelete={remove} />
        : <section className="fed-admin-card mc-admin-welcome"><Ic name="gamepad" size={42} /><h2>Выберите матч</h2><p>Откройте существующий матч слева или создайте новый. Все опубликованные изменения появятся у зрителей в течение пяти секунд.</p><div><button type="button" className="fed-btn-ghost" onClick={() => startCreate('cs2')}>Новый CS2 матч</button><button type="button" className="fed-btn-ghost" onClick={() => startCreate('mlbb')}>Новый MLBB матч</button></div></section>}
    </div>
  </div>;
}
